// Scene-wise Look & Feel — own GATED step (CD placement ruling 2026-07-15):
// lives between Shot Breakdown and Crew Pre-Production, and only opens once the
// breakdown is approved (scenes defined). Before that it shows a locked state,
// never empty scene rows.
//
// v2 (2026-07-15): each scene row is now CLICKABLE — it expands to reveal a
// per-scene reference upload panel (SceneRefsPanel) whose refs/decode are scoped
// to that scene and injected into that scene's keyframe prompts. Multiple rows
// can stay open at once. The project-wide Reference Library below acts as the
// default look applied to every scene (a scene's own refs override it).
import React, { useState } from 'react';
import { Lock, ChevronDown, ChevronRight, ImagePlus } from 'lucide-react';
import { LookFeelRefsPanel } from './LookFeelRefsPanel';
import { SceneRefsPanel } from './SceneRefsPanel';

export function SceneLookFeel({ projectId, shots = [] }) {
  const approved = (shots || []).length > 0;   // breakdown approved ⇒ scenes exist
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (sc) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(sc) ? next.delete(sc) : next.add(sc);
    return next;
  });

  if (!approved) {
    return (
      <div className="p-6 rounded-xl border border-dashed border-white/15 bg-white/[0.02] text-center" data-testid="lf-locked-state">
        <Lock className="w-5 h-5 text-white/30 mx-auto mb-2" />
        <div className="text-sm font-medium text-white/60">Scenes not defined yet — approve Shot Breakdown first</div>
        <div className="text-xs text-white/35 mt-1">Look &amp; Feel unlocks once your scenes are identified and locked.</div>
      </div>
    );
  }

  // Unique scenes in canonical order.
  const scenes = [];
  const seen = new Set();
  for (const s of shots) {
    const sc = s.scene_number;
    if (sc && !seen.has(sc)) { seen.add(sc); scenes.push(sc); }
  }
  const countFor = (sc) => shots.filter((s) => s.scene_number === sc).length;

  return (
    <div className="space-y-4" data-testid="lf-scene-step">
      <div>
        <div className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
          Per-scene look — click a scene to add references
        </div>
        <div className="space-y-1.5" data-testid="lf-scene-rows">
          {scenes.map((sc) => {
            const isOpen = expanded.has(sc);
            return (
              <div key={sc} data-testid={`lf-scene-row-${sc}`} className="rounded-lg bg-white/5 border border-white/10 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggle(sc)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.07] transition-colors"
                  data-testid={`lf-scene-toggle-${sc}`}
                >
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-white/50 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-white/50 shrink-0" />}
                  <span className="text-[12px] font-semibold text-white/80 w-16 shrink-0">{sc}</span>
                  <span className="text-[11px] text-white/40 w-16 shrink-0">{countFor(sc)} shot{countFor(sc) === 1 ? '' : 's'}</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-white/40">
                    <ImagePlus className="w-3 h-3" /> refs
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3">
                    <SceneRefsPanel projectId={projectId} scene={sc} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
          Project-wide default <span className="text-white/35 normal-case font-normal">· applied to every scene unless a scene has its own refs</span>
        </div>
        <LookFeelRefsPanel projectId={projectId} />
      </div>
    </div>
  );
}
